import type { CmmdWorkIntake } from "@cmmd-forge/contracts";

import { readPrimaryEnvironmentTarget, resolvePrimaryEnvironmentHttpUrl } from "./target";
import { CmmdApiError } from "../cmmdApi";
import { request, type TransportConfig } from "../cmmdApi/transport";

/**
 * Build a TransportConfig for the CMMD API transport that includes the
 * desktop cloud bearer when available. Uses the same single source of truth
 * as `buildPrimaryFetchInit`.
 */
export function buildPrimaryTransportConfig(
  base: Omit<TransportConfig, "bearerToken"> = { resolveUrl: resolvePrimaryEnvironmentHttpUrl },
): TransportConfig {
  const bearer = readPrimaryEnvironmentTarget()?.bearerToken;
  if (!bearer) return base;
  return { ...base, bearerToken: bearer };
}

export const CMMD_AUTH_ERROR_CODES = new Set(["CMMD_AUTH_REQUIRED", "CMMD_AUTH_EXPIRED"]);

/**
 * The work-intake query: `fresh=1` when forcing a server refresh, `orgId`
 * when a workspace is confirmed. Without an explicit org, the server nulls
 * `workspace` rather than guess (forge#3636), which empties Homebase
 * branding and the GitHub repo fallback for every account.
 */
function resolveWorkIntakeQuery(
  forceFresh: boolean,
  currentWorkspaceId: number | null,
): { readonly query: { readonly fresh?: 1; readonly orgId?: number } } | Record<string, never> {
  if (!forceFresh && currentWorkspaceId === null) return {};
  return {
    query: {
      ...(forceFresh ? { fresh: 1 as const } : {}),
      ...(currentWorkspaceId !== null ? { orgId: currentWorkspaceId } : {}),
    },
  };
}

async function fetchPrimaryCmmdWorkIntakeFromPath(forceFresh: boolean): Promise<CmmdWorkIntake> {
  const pathname = "/api/cmmd/work-intake";
  // Read lazily, not as a module import: environments/primary sits under
  // apps/web/src/store.ts's import graph (via cmmdApi/orchestration
  // wiring), and workspace/currentForgeWorkspaceStore.ts pulls in
  // cmmdForgeRuntimeListResource.ts, which imports store.ts back in. A
  // static import here closes that cycle; a dynamic one does not.
  const { getCurrentForgeWorkspaceId } = await import("../../workspace/currentForgeWorkspaceStore");
  const currentWorkspaceId = getCurrentForgeWorkspaceId();
  // Send the confirmed workspace the same way the other CMMD callers do
  // (ProfileSection, ForgeWorkspaceSwitcher, ...).
  let result: CmmdWorkIntake | undefined;
  try {
    result = await request<CmmdWorkIntake>(
      {
        method: "GET",
        path: pathname,
        allowRetry: false,
        ...resolveWorkIntakeQuery(forceFresh, currentWorkspaceId),
      },
      buildPrimaryTransportConfig(),
    );
  } catch (err) {
    if (
      err instanceof CmmdApiError &&
      err.kind !== "auth" &&
      err.code !== null &&
      CMMD_AUTH_ERROR_CODES.has(err.code)
    ) {
      throw new CmmdApiError({
        kind: "auth",
        message: err.message,
        status: err.status ?? 401,
        code: err.code,
      });
    }
    throw err;
  }
  if (!result) {
    throw new Error(`Invalid response from ${pathname}`);
  }
  return result;
}

export async function fetchPrimaryCmmdWorkIntake(
  options: { readonly forceFresh?: boolean } = {},
): Promise<CmmdWorkIntake> {
  return fetchPrimaryCmmdWorkIntakeFromPath(options.forceFresh === true);
}
